import { Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { firstValueFrom } from "rxjs";
import { UserProfile } from "src/app/subscription/services/auth/auth.interface";
import { AuthService } from "src/app/subscription/services/auth/auth.service";

type SupportFormModel = Pick<
  UserProfile,
  "amazon_wishlist_url" | "target_registry_url" | "walmart_registry_url" | "donorschoose_url"
>;

@Component({
  selector: "app-classroom-support-modal",
  standalone: true,
  imports: [ReactiveFormsModule, FormlyModule],
  template: `
    @if (model(); as model) {
      <div class="modal-header">
        <h4 class="modal-title">Edit Support Links</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modalRef.dismiss()"></button>
      </div>

      <form [formGroup]="form" (ngSubmit)="save()">
        <div class="modal-body">
          @if (error) {
            <div class="alert alert-danger" role="alert">{{ error }}</div>
          }
          <formly-form [form]="form" [fields]="formFields" [model]="model"></formly-form>
        </div>

        <div class="modal-footer d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-dark" (click)="modalRef.dismiss()">Cancel</button>
          <button type="submit" class="btn btn-primary" [disabled]="!form.valid || isLoading">
            @if (isLoading) {
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            }
            Save
          </button>
        </div>
      </form>
    }
  `,
})
export class ClassroomSupportModalComponent {
  private authService = inject(AuthService);
  modalRef = inject(NgbActiveModal);

  form = new FormGroup({});
  isLoading = false;
  error?: string;

  private user = toSignal(this.authService.$user, { initialValue: null });

  model = computed(() => {
    const user = this.user();
    if (!user) return null;
    return {
      amazon_wishlist_url: user.amazon_wishlist_url ?? null,
      target_registry_url: user.target_registry_url ?? null,
      walmart_registry_url: user.walmart_registry_url ?? null,
      donorschoose_url: user.donorschoose_url ?? null,
    };
  });

  formFields: FormlyFieldConfig[] = [
    {
      template: '<p class="text-muted small mb-3">Add links to your classroom wishlists and registries.</p>',
    },
    {
      key: "amazon_wishlist_url",
      type: "input",
      props: {
        label: "Amazon Wishlist",
        placeholder: "https://www.amazon.com/hz/wishlist/ls/...",
      },
    },
    {
      key: "target_registry_url",
      type: "input",
      props: {
        label: "Target Registry",
        placeholder: "https://www.target.com/gift-registry/...",
      },
    },
    {
      key: "walmart_registry_url",
      type: "input",
      props: {
        label: "Walmart Registry",
        placeholder: "https://www.walmart.com/registry/...",
      },
    },
    {
      key: "donorschoose_url",
      type: "input",
      props: {
        label: "DonorsChoose",
        placeholder: "https://www.donorschoose.org/project/...",
      },
    },
  ];

  async save() {
    if (!this.form.valid) return;

    this.isLoading = true;
    this.error = undefined;

    const user = this.user();
    if (!user) {
      this.error = "Not logged in";
      this.isLoading = false;
      return;
    }

    const formData = this.form.value as SupportFormModel;

    // Use null to explicitly clear empty values
    const profileData: Partial<UserProfile> = {
      amazon_wishlist_url: formData.amazon_wishlist_url || null,
      target_registry_url: formData.target_registry_url || null,
      walmart_registry_url: formData.walmart_registry_url || null,
      donorschoose_url: formData.donorschoose_url || null,
    };

    try {
      await firstValueFrom(this.authService.updateUserProfile(user.id, profileData));
      this.modalRef.close(true);
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : "Failed to save";
    } finally {
      this.isLoading = false;
    }
  }
}
